import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle2,
  Title1,
} from '@fluentui/react-components';
import { ArrowUndoRegular, CheckmarkCircleRegular } from '@fluentui/react-icons';
import { ApiService } from '../services/apiService';
import { useDevsPageStyles } from '../styles/DevsPage.styles';

interface PendingBenefitsPerTierChangeRow {
  id: string;
  scopeName: string;
  newScopeName: string | null;
  operation: string;
  payload: unknown;
  createdAt: string;
  createdBy: string;
}

export const DevsPage: React.FC = () => {
  const styles = useDevsPageStyles();
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  const api = useMemo(() => (account ? new ApiService(instance, account) : null), [instance, account]);

  const [rows, setRows] = useState<PendingBenefitsPerTierChangeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());

  const [pendingCounts, setPendingCounts] = useState<{ promotions: number; demotions: number } | null>(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [countsError, setCountsError] = useState<string | null>(null);

  useEffect(() => {
    if (!api) return;
    setCountsLoading(true);
    setCountsError(null);
    api.get<{ promotions: number; demotions: number }>('/TierChange/pendingSyncCounts')
      .then((data) => setPendingCounts(data))
      .catch((e) => setCountsError((e as Error).message ?? 'Failed to load counts'))
      .finally(() => setCountsLoading(false));
  }, [api]);

  const refresh = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<PendingBenefitsPerTierChangeRow[]>(
        '/ProgramConfig/pendingChanges/benefitsPerTier',
      );
      setRows(data ?? []);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load pending changes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markApplied = useCallback(
    async (id: string) => {
      if (!api) return;
      setBusyIds((prev) => new Set(prev).add(id));
      try {
        await api.post<void>(`/ProgramConfig/benefitsPerTier/${id}/markApplied`, undefined);
        setAppliedIds((prev) => new Set(prev).add(id));
      } catch (e) {
        setError((e as Error).message ?? 'Failed to mark applied');
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [api],
  );

  const undoApplied = useCallback(
    async (id: string) => {
      if (!api) return;
      setBusyIds((prev) => new Set(prev).add(id));
      try {
        await api.post<void>(`/ProgramConfig/benefitsPerTier/${id}/unmarkApplied`, undefined);
        setAppliedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } catch (e) {
        setError((e as Error).message ?? 'Failed to undo');
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [api],
  );

  if (!account) {
    return (
      <div className={styles.pageRoot}>
        <Body1>Please sign in to use the developer tools.</Body1>
      </div>
    );
  }

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <Title1>Developer tools</Title1>
      </div>

      {/* SRA Sync card */}
      <Card className={styles.card}>
        <div className={styles.cardHeader}>
          <Subtitle2>SRA Sync</Subtitle2>
          <Caption1 className={styles.subtitle}>
            The sync pipeline runs automatically on the 1st working day of each month.
            To trigger manually, set <strong>SyncBypassWorkingDayGuard=true</strong> in
            the Function App configuration, run the timer function from Azure Portal,
            then clear the setting.
          </Caption1>
        </div>

        {countsLoading ? (
          <Spinner size="tiny" label="Loading counts..." />
        ) : countsError ? (
          <MessageBar intent="error"><MessageBarBody>{countsError}</MessageBarBody></MessageBar>
        ) : (
          <div className={styles.countRow}>
            <div className={styles.countItem}>
              <span className={styles.countValue}>{pendingCounts?.promotions ?? 0}</span>
              <Body1Strong>Pending promotions</Body1Strong>
            </div>
            <div className={styles.countItem}>
              <span className={styles.countValue}>{pendingCounts?.demotions ?? 0}</span>
              <Body1Strong>Pending demotions</Body1Strong>
            </div>
          </div>
        )}
      </Card>

      {/* BenefitsPerTier pending changes card */}
      <Card className={styles.card}>
        <div className={styles.cardHeader}>
          <Subtitle2>Pending BenefitsPerTier changes</Subtitle2>
          <Caption1 className={styles.subtitle}>
            BenefitsPerTier changes are shipped manually to the SRA website. After you've
            deployed a staged change, click "Mark as Applied" so the "Last updated" pill
            in the regular UI reflects the deployment.
          </Caption1>
        </div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {loading ? (
          <Spinner label="Loading pending changes..." />
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>
            <Body1>No pending BenefitsPerTier changes.</Body1>
          </div>
        ) : (
          <div className={styles.table} role="table" aria-label="Pending BenefitsPerTier changes">
            <div className={styles.headerCell} role="columnheader">Benefit</div>
            <div className={styles.headerCell} role="columnheader">Operation</div>
            <div className={styles.headerCell} role="columnheader">Created at</div>
            <div className={styles.headerCell} role="columnheader">Created by</div>
            <div className={styles.headerCell} role="columnheader" />

            {rows.map((row) => {
              const displayName =
                row.operation.toLowerCase() === 'rename' && row.newScopeName
                  ? `${row.scopeName} → ${row.newScopeName}`
                  : row.scopeName;
              const isBusy = busyIds.has(row.id);
              const isApplied = appliedIds.has(row.id);
              return (
                <React.Fragment key={row.id}>
                  <Body1>{displayName}</Body1>
                  <Body1>{row.operation}</Body1>
                  <Body1>{new Date(row.createdAt).toLocaleString('en-US')}</Body1>
                  <Body1>{row.createdBy}</Body1>
                  {isApplied ? (
                    <Button
                      appearance="secondary"
                      icon={<ArrowUndoRegular />}
                      onClick={() => undoApplied(row.id)}
                      disabled={isBusy}
                    >
                      {isBusy ? 'Undoing…' : 'Undo'}
                    </Button>
                  ) : (
                    <Button
                      appearance="primary"
                      icon={<CheckmarkCircleRegular />}
                      onClick={() => markApplied(row.id)}
                      disabled={isBusy}
                    >
                      {isBusy ? 'Marking…' : 'Mark as Applied'}
                    </Button>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};
